import ListItemButton from '@mui/material/ListItemButton';
import { Typography, Grid, Box, Tooltip, useTheme } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import Barcode_Mini_Icon from '../../../../Assets/Barcode_Mini_Icon_White.png';
import { getShortUnitString } from '../../../../ts/Common';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LogoMark from '../../../../Assets/Logo_Mark_Shadow.svg';

type DimensionError = {
    code: string;
    description: string;
};

export default function TextScanEvent(props: any) {
    const { scanFailure, handleListItemClick, selectedScanIndex, cargoResponse, cargoResponseIndex, openDetailsModal, machine, open } = props
    const [rawImagePaths, setRawImagePaths] = useState<string[]>(['https://cargospectre.blob.core.windows.net/assets/images/cargo-placeholder.png']);
    const [certifiedDimension, setCertifiedDimensions] = useState<any[]>([true, true, true]);
    const { t } = useTranslation();
    const theme = useTheme();

    const styles = {
        length: {
            fontWeight: 750,
            color: '#50e279',
        },
        width: {
            fontWeight: 750,
            color: '#7777FF', // Periwinkle blue
        },
        height: {
            fontWeight: 750,
            color: '#ff5757',
        },
        lengthWidthHeightDims: {
            fontWeight: 750,
            color: '#FFFFFF',
        },
        lengthWidthHeightDimsOutOfRange: {
            fontWeight: 750,
            color: 'var(--spectre-warning-accent)',
        },
        barcode: {
            fontWeight: 450,
            color: '#FFFFFF',
            textAlign: 'center'
        },
        uploaded: {
            fontWeight: 450,
            color: '#FFFFFF',
            display: 'inline',
            float: 'right',
            mr: 1
        },
        failedX: {
            fontWeight: 600,
            color: '#FF0000',
            display: 'inline'
        },
        successCheck: {
            fontWeight: 600,
            color: '#1d9223ff',
            display: 'inline',
        },
        nullBox: {
            fontWeight: 600,
            display: 'inline'
        },
        x: {
            fontWeight: 750,
            color: '#262D6D',
            display: 'inline'
        },
        netText: {
            fontWeight: 450,
            color: '#FFFFFF',
            float: 'right',
            mr: 1
        },
        netWeightText: {
            fontWeight: 750,
            color: '#FFFFFF',
            float: 'right'
        },
        timeSinceText: {
            fontWeight: 550,
            color: '#FFFFFF',
            float: 'right',
            mr: 1
        },
        classMediumView: {
            fontWeight: 550,
            color: '#FFFFFF',
            float: 'right',
            mr: 1
        },
        barcodeImage: {
            ml: 1.5,
            backgroundColor: 'transparent',
        },
        scanFailureButton: {
            '&.Mui-disabled': {
                backgroundColor: '#F2BB2D',
            }
        },
        scanFailureText: {
            fontWeight: 750,
            color: '#1A1B23'
        },
        warningIcon: {
            color: 'var(--spectre-warning-accent)',
            fontSize: '15px',
            ml: 0.5,
            position: 'relative',
            top: '2px'
        }
    }

    const successCheck = (
        <Box component="span" sx={{ ...styles.successCheck, position: 'relative', top: '4px' }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="#1d9223ff" />
                <path d="M6 10.5L9 13.5L14 8.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </Box>
    )

    const failureCheck = (
        <Box component="span" sx={{ ...styles.failedX, position: 'relative', top: '4px' }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="#c23737" />
                <path d="M7 7 L13 13 M13 7 L7 13" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </Box>
    )

    const warningIcon = (
        <Tooltip title={t('control.centerControl.dimensions.outOfRangeShort')}>
            <WarningIcon 
                sx={styles.warningIcon}
            />
        </Tooltip>
    )

    useEffect(() => {
        // Event-history rows only need ONE small thumbnail. The full-resolution image set is
        // fetched on demand by CenterControl when a scan is selected. Loading every image here
        // caused an N-per-row fileprep round-trip and bloated memory during scroll.
        let cancelled = false;
        async function fetchThumbnail() {
            if (!cargoResponse?.fileList?.images || !Array.isArray(cargoResponse.fileList.images)) {
                return;
            }
            // Reuse a previously resolved thumbnail (survives virtualization remount).
            if (cargoResponse.thumbnailUrl) {
                setRawImagePaths([cargoResponse.thumbnailUrl]);
                return;
            }
            const firstImage = cargoResponse.fileList.images.find((image: string) => image && !image.includes('barcode'));
            if (!firstImage || !machine?.connection?.imageThumbnail) {
                return;
            }
            try {
                const thumbnail = await machine.connection.imageThumbnail(firstImage) as string;
                if (cancelled) return;
                cargoResponse.thumbnailUrl = thumbnail;
                setRawImagePaths([thumbnail]);
            } catch {
                // Leave the placeholder background if the thumbnail fails to resolve.
            }
        }
        fetchThumbnail();
        return () => { cancelled = true; };
    }, [cargoResponse, machine?.connection]);

    useEffect(() => {
        if (cargoResponse?.dimensions?.dimensionBounds) {
            const dimensionBounds = cargoResponse.dimensions.dimensionBounds;
            setCertifiedDimensions([dimensionBounds.lengthCertified, dimensionBounds.widthCertified, dimensionBounds.heightCertified]);
        } else {
            setCertifiedDimensions([true, true, true]);
        }
    }, [cargoResponse]);

    // Check if any dimension is out of certified range
    const hasOutOfRangeDimensions = certifiedDimension.some(cert => !cert) || cargoResponse?.dimensions?.dimensionBounds.overallCertified === false;

    return (
        <ListItemButton
            selected={cargoResponseIndex === selectedScanIndex}
            onClick={(event) => {
                handleListItemClick(event, cargoResponseIndex)
                openDetailsModal()
            }}
            sx={{
                position: 'relative',
                backgroundImage: rawImagePaths[0] ? `linear-gradient(to right, rgba(0, 0, 0, 0.7), transparent, rgba(0, 0, 0, 0.7)), url(${rawImagePaths[0]})` : `url(${LogoMark})`,
                backgroundSize: scanFailure && !rawImagePaths[0] ? "contain" : "cover",
                borderRadius: '12px',
                backgroundPosition: {
                    sm: scanFailure ? 'center' : '0% 0%',
                    xs: open ? 'top' : 'center'
                },
                backgroundRepeat: 'no-repeat',
                backgroundColor: '#8d8d8dff',
                border: '1px solid rgba(255, 255, 255, 0.9)',
                mb: 2,
                mt: -1,
                px: 0,
                '@media (hover: hover)': theme.palette.mode === 'dark' ? {
                    '&:hover': {
                        boxShadow: 'inset 0 0 0 1px #FFFFFF',
                    },
                } : undefined,
                // Only kick in these border rules when in MUI large screen size or greater
                '@media (min-width: 1200px)': {
                    "&.Mui-selected": {
                        boxShadow: 'inset 0 0 0 4px #F8B160',
                    },
                    "&.Mui-selected:hover": {
                        boxShadow: 'inset 0 0 0 4px #F7914B, 0 0 25px rgba(255, 255, 255, 0.6)',
                    },
                    '&:hover': {
                        border: 0.2,
                    },
                },
            }}
            disableGutters
        >
            {scanFailure && (
                <>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 8,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#F2BB2D',
                            color: '#1A1B23',
                            fontWeight: 750,
                            fontSize: '0.875rem',
                            px: 2,
                            py: 0.5,
                            borderRadius: '4px',
                            zIndex: 10,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                    >
                        {t('control.eventHistory.scanDetails.scanFailure') || 'Scan Failure'}
                    </Box>
                    {cargoResponse?.dimensionErrors && Array.isArray(cargoResponse.dimensionErrors) && cargoResponse.dimensionErrors.length > 0 && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 42,
                                left: '50%',
                                backgroundColor: '#c23737ff',
                                borderRadius: '4px',
                                transform: 'translateX(-50%)',
                                color: '#FFFFFF',
                                fontSize: '0.75rem',
                                fontWeight: 400,
                                textAlign: 'center',
                                zIndex: 10,
                                px: 1
                            }}
                        >
                            {cargoResponse.dimensionErrors.map((error: DimensionError, index: number) => (
                                <span key={error?.code || index}>
                                    {error?.code || 'Unknown Error'}
                                    {index < cargoResponse.dimensionErrors.length - 1 ? ', ' : ''}
                                </span>
                            ))}
                        </Box>
                    )}
                </>
            )}
            {hasOutOfRangeDimensions && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#F2BB2D',
                        color: '#1A1B23',
                        fontWeight: 750,
                        fontSize: '0.875rem',
                        px: 2,
                        py: 0.5,
                        borderRadius: '4px',
                        zIndex: 10,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                >
                    {t('control.eventHistory.scanDetails.uncertified')}
                </Box>
            )}
            <Grid container
                sx={{
                    mt: {
                        sm: 10,
                        xs: open ? 10 : 0,
                    }
                }}
            >

                <Grid container direction="row" justifyContent="space-between" alignItems="flex-end">
                    {
                        cargoResponse?.dimensions?.freightClass?.type ?
                            <>
                                <Grid
                                    item
                                    md={12}
                                    xs={12}
                                    sx={{
                                        display: { xs: 'none', sm: 'flex' },
                                        justifyContent: 'flex-end',
                                        alignItems: 'flex-end'
                                    }}
                                >
                                    <Typography component={'span'} sx={styles.classMediumView}>
                                        {t('control.eventHistory.scanDetails.class')}: {cargoResponse?.dimensions?.freightClass?.class || '--'}
                                    </Typography>
                                </Grid>
                                {open && (<Grid
                                    item
                                    md={12}
                                    xs={12}
                                    sx={{
                                        display: { xs: 'flex', sm: 'none' },
                                        justifyContent: 'flex-end',
                                        alignItems: 'flex-end'
                                    }}
                                >
                                    <Typography component={'span'} sx={{ ...styles.timeSinceText, display: { xs: 'inline', sm: 'none' } }}>
                                        {t('control.eventHistory.scanDetails.class')}: {cargoResponse?.dimensions?.freightClass?.class || '--'}
                                    </Typography>
                                </Grid>)}
                                <Grid item xs={1}>
                                    <Typography component={'span'} noWrap sx={{ textAlign: 'center' }}>
                                        {(cargoResponse?.barcode && cargoResponse.barcode !== '') ?
                                            <Box
                                                component="img"
                                                sx={styles.barcodeImage}
                                                alt={t('control.eventHistory.scanDetails.barcode') || ""}
                                                src={Barcode_Mini_Icon}
                                            /> : <Box />
                                        }
                                    </Typography>
                                </Grid>
                                <Grid item xs={3} sm={5}>
                                    <Typography component={'span'} noWrap sx={styles.barcode}>
                                        {cargoResponse?.barcode?.length <= 14 ? cargoResponse.barcode : (cargoResponse?.barcode?.substr(0, 11) + '...')}
                                    </Typography>
                                </Grid>
                                <Grid
                                    item
                                    md={0}
                                    xs={4}
                                    sx={{
                                        display: { xs: 'flex', sm: 'none' },
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}
                                >
                                    {!open && (
                                        <Typography component={'span'} sx={{ ...styles.timeSinceText, display: { xs: 'inline', sm: 'none' } }}>
                                            {t('control.eventHistory.scanDetails.class')}: {cargoResponse?.dimensions?.freightClass?.class || '--'}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item sm={6} xs={4}>
                                    <Typography component={'span'} sx={styles.netText}>{t('control.eventHistory.scanDetails.weight')}:&nbsp;<Typography component={'span'} sx={styles.netWeightText}>{cargoResponse?.dimensions?.weight?.display} {cargoResponse?.units?.weight || ''}</Typography></Typography>
                                </Grid>
                            </>
                            :
                            <>
                                <Grid item xs={1}>
                                    <Typography component={'span'} noWrap sx={{ textAlign: 'center' }}>
                                        {(cargoResponse?.barcode && cargoResponse.barcode !== '') ?
                                            <Box
                                                component="img"
                                                sx={styles.barcodeImage}
                                                alt={t('control.eventHistory.scanDetails.barcode') || ""}
                                                src={Barcode_Mini_Icon}
                                            /> : <Box />
                                        }
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography component={'span'} noWrap sx={styles.barcode}>
                                        {cargoResponse?.barcode?.length <= 25 ? cargoResponse?.barcode : (cargoResponse?.barcode?.substr(0, 25) + '...')}
                                    </Typography>
                                </Grid>
                                <Grid item md={6} xs={6}>
                                    <Typography component={'span'} sx={styles.netText}>{t('control.eventHistory.scanDetails.weight')}:&nbsp;<Typography component={'span'} sx={styles.netWeightText}>{cargoResponse?.dimensions?.weight?.display} {cargoResponse?.units?.weight || ''}</Typography></Typography>
                                </Grid>
                            </>
                    }
                    <Grid item xs={1} sx={{ textAlign: 'center' }}>
                        <Typography component={'span'} sx={styles.length}>{t('control.eventHistory.scanDetails.lengthShorthand')}</Typography>
                    </Grid>
                    <Grid item xs={5}>
                        {cargoResponse?.dimensions?.length == null
                            ? (<Typography component={'span'} sx={styles.lengthWidthHeightDims}>--</Typography>)
                            : (<><Typography component={'span'} sx={certifiedDimension[0] ? styles.lengthWidthHeightDims : styles.lengthWidthHeightDimsOutOfRange}>{cargoResponse.dimensions.length} {getShortUnitString(cargoResponse?.units?.length || '')}</Typography>{!certifiedDimension[0] && warningIcon}</>)
                        }
                    </Grid>
                    <Grid item xs={6}>
                        {/* <Typography component={'span'} sx={styles.netText}>{t('control.eventHistory.scanDetails.weight')}:&nbsp;<Typography component={'span'} sx={styles.netWeightText}>{cargoResponse.dimensions.weight.display} {cargoResponse.units.weight}</Typography></Typography> */}
                        <Typography component={'span'} sx={styles.uploaded}>{t('control.eventHistory.scanDetails.uploaded')}: {cargoResponse?.uploaded == null ? <Typography sx={styles.nullBox}>&#9744;</Typography> : cargoResponse.uploaded ? successCheck : failureCheck
                        }</Typography>
                    </Grid>
                    <Grid item xs={1} sx={{ textAlign: 'center' }}>
                        <Typography component={'span'} sx={styles.width}>{t('control.eventHistory.scanDetails.widthShorthand')}</Typography>
                    </Grid>
                    <Grid item xs={5}>
                        {cargoResponse?.dimensions?.width == null
                            ? (<Typography component={'span'} sx={styles.lengthWidthHeightDims}>--</Typography>)
                            : (<><Typography component={'span'} sx={certifiedDimension[1] ? styles.lengthWidthHeightDims : styles.lengthWidthHeightDimsOutOfRange}>{cargoResponse.dimensions.width}  {getShortUnitString(cargoResponse?.units?.length || '')}</Typography>{!certifiedDimension[1] && warningIcon}</>)
                        }
                    </Grid>
                    <Grid item xs={6}>
                        <Typography component={'span'} sx={styles.uploaded}>{t('control.eventHistory.scanDetails.exported')}: {cargoResponse?.exported == null ? <Typography sx={styles.nullBox}>&#9744;</Typography> : cargoResponse.exported ? successCheck : failureCheck}</Typography>
                    </Grid>
                    <Grid item xs={1} sx={{ textAlign: 'center' }}>
                        <Typography component={'span'} sx={styles.height}>{t('control.eventHistory.scanDetails.heightShorthand')}</Typography>
                    </Grid>
                    <Grid item xs={5}>
                        {cargoResponse?.dimensions?.height == null
                            ? (<Typography component={'span'} sx={styles.lengthWidthHeightDims}>--</Typography>)
                            : (<><Typography component={'span'} sx={certifiedDimension[2] ? styles.lengthWidthHeightDims : styles.lengthWidthHeightDimsOutOfRange}>{cargoResponse.dimensions.height}  {getShortUnitString(cargoResponse?.units?.length || '')}</Typography>{!certifiedDimension[2] && warningIcon}</>)
                        }
                    </Grid>
                    <Grid item xs={6}>
                        <Typography component={'span'} sx={styles.timeSinceText} noWrap> {cargoResponse?.timeStamp?.toLocaleDateString() ?? '--'} {cargoResponse?.timeStamp?.toLocaleTimeString() ?? '--'}</Typography>
                    </Grid>
                </Grid>
            </Grid>
        </ListItemButton>
    )
}